
import React from 'react';
/* Added ArrowRight to the imports from lucide-react */
import {
  BarChart3,
  LayoutDashboard,
  ArrowRight,
  ArrowUpRight,
  FileText,
  Package,
  ShieldCheck,
  Zap,
  Calendar,
  Layers,
  Sparkles
} from 'lucide-react';

const FinancialAppDetail: React.FC = () => {
  return (
    <div className="pt-24 pb-20 bg-white">
      {/* Hero */}
      <section className="container mx-auto px-4 md:px-8 mb-20">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-bold uppercase tracking-wider">
            <ShieldCheck size={16} /> Sesuai Standar SAK EMKM
          </div>
          <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 leading-tight">
            Kelola Keuangan Bisnis <br /> <span className="text-blue-600">Semudah Balas Chat</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Dirancang khusus untuk pemilik UMKM yang ingin fokus jualan, bukan pusing urusan pembukuan. Otomatis, akurat, dan sangat mudah.
          </p>
        </div>

        {/* Mockup Preview */}
        <div className="mt-16 relative max-w-5xl mx-auto">
          <div className="bg-slate-900 rounded-[2.5rem] p-4 shadow-2xl overflow-hidden border-8 border-slate-800">
            <div className="bg-white rounded-[2rem] overflow-hidden min-h-[500px] flex flex-col md:flex-row">
              {/* Sidebar Mockup */}
              <div className="w-64 bg-slate-50 border-r border-slate-100 p-6 hidden md:block">
                <div className="h-4 w-32 bg-slate-200 rounded-full mb-8"></div>
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className={`h-8 w-full rounded-lg ${i === 1 ? 'bg-blue-100' : 'bg-slate-100'}`}></div>
                  ))}
                </div>
              </div>
              {/* Content Mockup */}
              <div className="flex-1 p-8">
                <div className="flex justify-between items-center mb-10">
                  <div className="h-6 w-40 bg-slate-100 rounded-full"></div>
                  <div className="h-10 w-10 bg-blue-600 rounded-full"></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="p-6 bg-blue-50 rounded-2xl">
                    <p className="text-xs text-blue-600 font-bold mb-2">SALDO SAAT INI</p>
                    <p className="text-2xl font-bold text-slate-900">Rp 45.250.000</p>
                  </div>
                  <div className="p-6 bg-slate-50 rounded-2xl">
                    <p className="text-xs text-slate-400 font-bold mb-2">TOTAL PENJUALAN</p>
                    <p className="text-2xl font-bold text-slate-900">Rp 128.500.000</p>
                  </div>
                </div>
                <div className="h-48 w-full bg-slate-50 rounded-3xl border border-dashed border-slate-200 flex items-center justify-center">
                  <BarChart3 className="text-slate-200" size={48} />
                </div>
              </div>
            </div>
          </div>
          {/* Floating Element */}
          <div className="absolute -bottom-6 -right-6 md:-right-12 bg-white p-6 rounded-3xl shadow-2xl border border-slate-100 max-w-xs animate-bounce duration-[4s]">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-green-100 p-2 rounded-xl text-green-600">
                <ArrowUpRight size={20} />
              </div>
              <p className="text-sm font-bold">Laba Bersih Naik!</p>
            </div>
            <p className="text-xs text-slate-500">Bulan ini laba Anda meningkat 12% dibandingkan bulan lalu.</p>
          </div>
        </div>
      </section>

      {/* Features Detail */}
      <section className="bg-slate-50 py-24">
        <div className="container mx-auto px-4 md:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <h2 className="text-3xl font-bold text-slate-900">Fitur yang Memudahkan Bisnis Anda</h2>
              <div className="space-y-6">
                <FeatureItem 
                  icon={<Zap className="text-yellow-500" />}
                  title="Input Transaksi Kilat"
                  description="Cukup masukkan nominal dan kategori, jurnal otomatis akan terbentuk di balik layar. Tidak perlu belajar debit-kredit."
                />
                <FeatureItem 
                  icon={<Package className="text-blue-500" />}
                  title="Manajemen Inventori Pintar"
                  description="Stok berkurang otomatis saat ada penjualan. Dapatkan peringatan stok rendah sebelum barang habis."
                />
                <FeatureItem 
                  icon={<ShieldCheck className="text-green-500" />}
                  title="Sesuai Standar Akuntansi"
                  description="Seluruh pencatatan mengacu pada standar SAK EMKM, sehingga laporan Anda diakui untuk pengajuan modal/bank."
                />
              </div>
            </div>
            <div className="bg-white p-10 rounded-[3rem] shadow-xl border border-slate-100">
              <h3 className="text-2xl font-bold mb-8 flex items-center gap-3">
                <FileText className="text-blue-600" /> Laporan Real-time
              </h3>
              <div className="space-y-6">
                <div className="p-4 rounded-2xl bg-slate-50 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-white p-2 rounded-lg shadow-sm"><LayoutDashboard size={20} className="text-slate-600" /></div>
                    <span className="font-bold text-slate-700">Laporan Laba Rugi</span>
                  </div>
                  {/* ArrowRight is now available from imports */}
                  <ArrowRight size={18} className="text-slate-400" />
                </div>
                <div className="p-4 rounded-2xl bg-slate-50 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-white p-2 rounded-lg shadow-sm"><Layers size={20} className="text-slate-600" /></div>
                    <span className="font-bold text-slate-700">Arus Kas (Cash Flow)</span>
                  </div>
                  {/* ArrowRight is now available from imports */}
                  <ArrowRight size={18} className="text-slate-400" />
                </div>
                <div className="pt-6 border-t border-slate-100">
                  <p className="text-sm text-slate-500 mb-4 font-medium uppercase tracking-widest flex items-center gap-2">
                    <Calendar size={14} /> Pilih Periode Waktu
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <button className="py-2 px-4 rounded-xl border border-slate-200 text-xs font-bold hover:bg-blue-600 hover:text-white transition-all">Bulanan</button>
                    <button className="py-2 px-4 rounded-xl border border-slate-200 text-xs font-bold hover:bg-blue-600 hover:text-white transition-all">Kuartal</button>
                    <button className="py-2 px-4 rounded-xl border border-slate-200 text-xs font-bold hover:bg-blue-600 hover:text-white transition-all">Semester</button>
                    <button className="py-2 px-4 rounded-xl bg-blue-600 text-white text-xs font-bold">Tahunan</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24">
        <div className="container mx-auto px-4 md:px-8 text-center max-w-4xl">
          <div className="bg-blue-50 p-12 md:p-20 rounded-[4rem] space-y-8 relative overflow-hidden">
            <Sparkles className="absolute top-10 right-10 text-blue-200" size={64} />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">Kenapa Kami Berbeda?</h2>
            <p className="text-lg text-slate-600 leading-relaxed italic">
              "Banyak aplikasi akuntansi dibuat oleh akuntan untuk akuntan. DigiMaju dibuat oleh pengembang yang memahami lelahnya menjadi pengusaha UMKM. Fokus kami adalah memberi Anda visibilitas bisnis tanpa kerumitan administrasi."
            </p>
            <div className="flex justify-center gap-4 pt-6">
              <button className="px-10 py-4 bg-blue-600 text-white rounded-2xl font-bold shadow-lg hover:bg-blue-700 transition-all">
                Coba Gratis 14 Hari
              </button>
              <button className="px-10 py-4 bg-white text-slate-700 border border-slate-200 rounded-2xl font-bold hover:bg-slate-50 transition-all">
                Tanya Konsultan
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

const FeatureItem: React.FC<{ icon: React.ReactNode, title: string, description: string }> = ({ icon, title, description }) => (
  <div className="flex gap-6 group">
    <div className="shrink-0 w-14 h-14 bg-white rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center group-hover:shadow-md group-hover:scale-110 transition-all duration-300">
      {icon}
    </div>
    <div>
      <h4 className="text-xl font-bold text-slate-900 mb-2">{title}</h4>
      <p className="text-slate-500 leading-relaxed">{description}</p>
    </div>
  </div>
);

export default FinancialAppDetail;
